import React from 'react';
import { styled } from '@linaria/react';

import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';
import { normalize } from '@/utils';

import Demo, { DemoSliderControl } from '@/components/Demo';
import Aside from '@/components/Aside';

const OldMethod = () => {
  const prefersReducedMotion = usePrefersReducedMotion();

  return (
    <>
      <Demo
        id="old-rainbow-method"
        initialValues={{ rotation: 0 }}
        outputWrapper={OutputWrapper}
        controlPosition="top"
        customGrid={{
          gridTemplateColumns: 'min(360px, 100%)',
          justifyContent: 'center',
        }}
        controls={[
          <DemoSliderControl
            key="rotation"
            id="rotation"
            label="Reveal"
            min={0}
            max={70}
          />,
        ]}
      >
        {({ rotation }) => {
          const opacity = normalize(rotation, 0, 80, 1, 0.75);
          return (
            <Parent>
              <Wrapper
                style={{
                  transform: `rotateY(${rotation * -0.5}deg)`,
                }}
              >
                <Front
                  data-include-shadow={rotation > 0}
                  style={{
                    transform: `translateZ(${rotation * 0.2}px)`,
                  }}
                >
                  <Backdrop style={{ height: 100, opacity }} />
                  <Row>
                    <Backdrop style={{ height: 60, opacity }} />
                    <Button>Subscribe</Button>
                    <Backdrop style={{ height: 60, opacity }} />
                  </Row>
                  <Backdrop style={{ height: 100, opacity }} />
                </Front>

                <Back
                  style={{
                    transform: `translateZ(-${rotation * 0.2}px)`,
                  }}
                >
                  <RainbowGradient />
                </Back>
              </Wrapper>
            </Parent>
          );
        }}
      </Demo>
      {prefersReducedMotion && (
        <Aside type="warning">
          It appears that you've opted to reduce motion, in your
          Operating System settings. This blog tries to respect this
          query, which means that these demos have been disabled,
          since they rely heavily on motion.
        </Aside>
      )}
    </>
  );
};

export const LEGACY_COLORS = {
  pink: {
    100: '#FF80AB',
    300: '#FF4081',
    500: '#f40088',
    700: '#cc0072',
  },
  red: {
    500: '#EF5350',
    700: '#D50000',
  },
  orange: {
    500: '#FF9100',
    700: '#FF6D00',
  },
  yellow: {
    500: '#FFC400',
    700: '#FFAB00',
  },
  lime: {
    500: '#C6FF00',
    700: '#AEEA00',
  },
  green: {
    500: '#00E676',
    700: '#00C853',
  },
  blue: {
    500: '#3D5AFE',
    700: '#304FFE',
  },
  indigo: {
    500: '#000bef',
    700: '#0000e0',
  },
  violet: {
    500: '#D500F9',
    700: '#AA00FF',
  },
  purple: {
    500: '#651fff',
    700: '#4919b7',
  },
  gray: {
    50: '#f8f8f8',
    100: '#f2f2f2',
    200: '#eaeaea',
    300: '#cccccc',
    400: '#aaaaaa',
    500: '#888888',
    600: '#666666',
    700: '#444444',
    800: '#2A2A2A',
    900: '#111111',
  },
  white: '#FFF',
  black: '#000',
};

const OutputWrapper = styled.div`
  position: relative;
  width: 100%;
  min-height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  overflow: hidden;
  border-radius: 4px;
  background: white;
`;

const Parent = styled.div`
  flex: 1;
  width: 100%;
  max-width: 300px;
  perspective: 500px;
  transform-style: preserve-3d;
`;

const Wrapper = styled.div`
  position: relative;
  height: 260px;
  transform-style: preserve-3d;
  perspective: 500px;
  transform-origin: center center;
`;

const Backdrop = styled.div`
  background: #fff;
`;

const Row = styled.div`
  display: flex;

  div {
    flex: 1;
  }

  button {
    flex: 2;
  }
`;

const Button = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent;
  height: 60px;
  border: none;
  color: white;
  font-size: 21px;
`;

const Front = styled.div`
  position: absolute;
  z-index: 2;
  width: 100%;
  height: 100%;
  transform-origin: center center;

  &[data-include-shadow='true'] {
    box-shadow:
      0 0.9px 1.2px rgba(0, 0, 0, 0.05),
      0 3.5px 3.4px rgba(0, 0, 0, 0.027),
      0 10.8px 8.1px rgba(0, 0, 0, 0.02),
      0 46px 27px rgba(0, 0, 0, 0.014);
  }
`;
const Back = styled.div`
  position: absolute;
  z-index: 1;
  width: 100%;
  height: 100%;
  transform-origin: center center;
`;

const RainbowGradient = styled.div`
  position: absolute;
  top: 0;
  left: 10%;
  right: 10%;
  bottom: 0;
  display: block;
  height: 400px;
  background: linear-gradient(
    -188deg,
    ${LEGACY_COLORS.red[500]},
    ${LEGACY_COLORS.orange[500]},
    ${LEGACY_COLORS.yellow[700]},
    ${LEGACY_COLORS.green[500]},
    ${LEGACY_COLORS.blue[500]},
    ${LEGACY_COLORS.purple[500]},
    ${LEGACY_COLORS.pink[500]},
    ${LEGACY_COLORS.red[500]},
    ${LEGACY_COLORS.orange[500]},
    ${LEGACY_COLORS.yellow[700]},
    ${LEGACY_COLORS.green[500]},
    ${LEGACY_COLORS.blue[500]}
  );
  animation: gradientGo 6s linear infinite;

  @keyframes gradientGo {
    from {
      transform: translateY(25%);
    }
    to {
      transform: translateY(-45%);
    }
  }
`;

export default OldMethod;
